import { ReviewStats } from '../../../sdk/@types/Revisao';

export type Maturity = ReviewStats['maturity'];

/**
 * Maturity is an ordered progression (new → learning → young → mature), so it gets a
 * sequential single-hue ramp, light→dark — not categorical hues.
 *
 * The steps are re-stepped off the house green ramp so adjacent pairs clear the ΔE 15
 * normal-vision floor: the stock `heat1`↔`heat2` pair sits at 13.0, which readers with
 * full colour vision struggle to separate side by side. Lightness is strictly
 * monotonic (0.935 → 0.779 → 0.580 → 0.393). Verified with the dataviz validator:
 * CVD separation ΔE 14.4 worst adjacent (protan), normal-vision 15.9 worst adjacent.
 *
 * Re-checked against the Pencil frames' own `heat-0,1,2,4` ramp (no `heat-3`) when
 * `--c-chart-strong` was added (app-shell-pencil-palette-and-home-order): that ramp's
 * 0↔1 pair measures ΔE 6.4 light / 10.8 dark, and 1↔2 measures 11.5 light — both
 * under the 15 floor, so it can't supply four stops clearing the floor at every
 * adjacent step in either theme. This ramp stays unchanged; see src/index.css next
 * to --c-chart-strong for the full pair-by-pair numbers.
 */
export const MATURITY: { key: keyof Maturity; label: string; color: string }[] = [
  { key: 'new', label: 'Novos', color: '#d9f0e3' },
  { key: 'learning', label: 'Aprendendo', color: '#82ca9d' },
  { key: 'young', label: 'Jovens', color: '#3d9b6e' },
  { key: 'mature', label: 'Maduros', color: '#14532d' },
];

export interface MaturityBarProps {
  maturity: Maturity;
}

/**
 * One stacked bar = the deck's composition across the four SM-2 states.
 *
 * The pale end of the ramp sits under 3:1 against the card surface, so identity and
 * magnitude never rest on the fill alone: every state is named in the legend and
 * repeated with its count below, and wide-enough segments carry an inline label.
 * 2px surface gaps separate adjacent fills.
 */
export default function MaturityBar({ maturity }: MaturityBarProps) {
  const total = MATURITY.reduce((sum, m) => sum + (maturity?.[m.key] ?? 0), 0);

  if (!total) {
    return (
      <p className="py-6 text-center text-sm text-slate-400 dark:text-slate-400">
        Nenhum cartão na revisão ainda.
      </p>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1">
        {MATURITY.map((m) => (
          <span
            key={m.key}
            className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"
          >
            <span
              style={{ background: m.color }}
              className="inline-block h-2.5 w-2.5 rounded-sm ring-1 ring-slate-300 dark:ring-slate-600"
            />
            {m.label}
          </span>
        ))}
      </div>

      <div className="flex h-7 w-full gap-[2px] overflow-hidden rounded-lg ring-1 ring-slate-200 dark:ring-slate-700">
        {MATURITY.map((m) => {
          const v = maturity?.[m.key] ?? 0;
          if (!v) return null;
          const pct = (v / total) * 100;
          return (
            <div
              key={m.key}
              title={`${m.label}: ${v} (${Math.round(pct)}%)`}
              style={{ background: m.color, width: `${pct}%` }}
              className="flex min-w-0 items-center justify-center"
            >
              {/* a sliver can't hold a legible number — the row below always carries it */}
              {pct >= 8 && (
                <span
                  className={`truncate px-1 text-[10px] font-bold tabular-nums ${
                    m.key === 'mature' ? 'text-white' : 'text-slate-800'
                  }`}
                >
                  {v}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {MATURITY.map((m) => (
          <span key={m.key} className="text-xs text-slate-500 dark:text-slate-400">
            {m.label}{' '}
            <strong className="tabular-nums text-slate-700 dark:text-slate-200">
              {maturity?.[m.key] ?? 0}
            </strong>
          </span>
        ))}
        <span className="ml-auto text-xs tabular-nums text-slate-400 dark:text-slate-500">
          {total} no total
        </span>
      </div>
    </div>
  );
}
